import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Map,
  Upload,
  Settings,
  X,
  Calendar,
  User,
  ChevronRight,
  ReceiptText
} from 'lucide-react';
import { useSupabase } from '../../context/SupabaseContext';
import { NavItem } from '../interfaces/All';
interface SidebarProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}


const Sidebar: React.FC<SidebarProps> = ({ isOpen, setIsOpen }) => {
  
  let navItems: NavItem[] = [
    {
      title: 'Dashboard',
      path: '/dashboard',
      icon: <LayoutDashboard size={20} />
    },
    {
      title: 'Wallet',
      path: '/ledger',
      icon: <ReceiptText size={20} />
    },
    {
      title: 'Lessons',
      path: '/lesson',
      icon: <Calendar size={20} />
    },
    {
      title: 'Students',
      path: '/students',
      icon: <User size={20} />
    },
    {
      title: 'Settings',
      path: '/settings',
      icon: <Settings size={20} />
    },
    {
      title: 'Forms',
      path: '/forms',
      icon: <Settings size={20} />
    }
  ];

  const { state, fetchUserRole } = useSupabase();

  if(state.userRole=='admin'){
    navItems = [...navItems,
      {
        title: 'Contacts',
        path: undefined,
        items: [
          {
            title: 'All Parents',
            path: '/parents',
            icon: <User size={20} />
          },
          {
            title: 'All Students',
            path: '/students',
            icon: <User size={20} />
          },
          {
            title: 'All Teachers',
            path: '/teachers',
            icon: <User size={20} />
          },
        ],
        icon: <User size={20} />
      },
    ]
  }
  if(state.userRole=='teacher'){
    navItems = [...navItems,
      {
        title: 'Contacts',
        path: undefined,
        items: [
          {
            title: 'Parents',
            path: '/parents',
            icon: <User size={20} />
          },
          {
            title: 'My Students',
            path: '/students',
            icon: <User size={20} />
          },
        ],
        icon: <User size={20} />
      },
    ]
  }

  const NavItem = ({ item, level = 0 }: { item: NavItem; level?: number }) => {
    return (
      <>
        {/* Main item */}
        <li className={`pl-${level * 4}`} >
          {item.path ? (
            <NavLink
              to={item.path}
              className={({ isActive }) => `
                flex items-center gap-3 px-4 py-3 rounded-lg transition-all
                ${isActive
                  ? 'bg-primary text-white'
                  : 'text-text-light hover:bg-background hover:text-text'
                }
                ${level > 0 ? 'pl-8 ml-2' : ''}
              `}
              end={item.path === '/'}
            >
              {level === 0 && item.icon}  {/* Icon only on main level */}
              <span className={`font-medium ${level > 0 ? 'text-sm' : ''}`}>
                {item.title}
              </span>
            </NavLink>
          ) : (
            <div key={item.title}
              className="flex items-center gap-3 px-4 py-3 rounded-lg transition-all text-text-light hover:bg-background hover:text-text"
            >
              {level === 0 && item.icon}  {/* Icon only on main level */}
              {item.title}
            </div>
          )}

        </li>

        {/* 👈 Indented subitems (no toggle) */}
        {item.items?.map((subItem) => (
          <NavItem key={subItem.path} item={subItem} level={level + 1} />
        ))}
      </>
    );
  };

  // Updated main nav
  <nav className="p-4">
    <ul className="space-y-2">
      {navItems.map((item) => (
        <NavItem key={item.path || item.title } item={item} />
      ))}
    </ul>
  </nav>

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-20 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 z-30 h-full w-64 bg-card border-r border-border transform transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:relative lg:translate-x-0
        `}
      >
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="bg-dark text-dark p-1.5 rounded-lg">
              <img src="/icons/logo.svg" className="dark:invert" width={20} alt="Logo" />
            </div>
            <h2 className="text-xl font-bold">Kite</h2>
          </div>

          <button
            className="p-1.5 rounded-lg hover:bg-background lg:hidden"
            onClick={() => setIsOpen(false)}
          >
            <X size={20} />
          </button>
        </div>

        <nav className="p-4">
          <ul className="space-y-2">
            {navItems.map((item) => (
              <NavItem key={item.path || item.title} item={item} />
            ))}
          </ul>
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-border">
          <div className="text-sm text-text-light">
            <p>Kite v0.1.0</p>
            <p className="mt-1">Local storage: {calculateStorageUsage()}</p>
          </div>
        </div>
      </aside>
    </>
  );
};

// Calculate approximate localStorage usage
const calculateStorageUsage = (): string => {
  try {
    const gpxRoutes = localStorage.getItem('gpxRoutes');
    if (!gpxRoutes) return '0 KB';

    const bytes = new Blob([gpxRoutes]).size;
    if (bytes < 1024) {
      return `${bytes} B`;
    } else if (bytes < 1048576) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    } else {
      return `${(bytes / 1048576).toFixed(1)} MB`;
    }
  } catch (error) {
    return 'Unknown';
  }
};

export default Sidebar;
